import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import ColegioService from '../services/ColegioService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import UserSessionMenu from './common/UserSessionMenu';
import AdminMenu from './common/menus/AdminMenu';
import { useAuth } from '../contexts/AuthContext';
import areaService from '../services/AreaService';
import usuarioService from '../services/UsuarioService';
import { ADMIN_FORM_MENU_ITEMS, navigateAdminMenuItem } from '../utils/adminMenu';

const AdminEditArea = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { areaId } = useParams();
  const { user, logout } = useAuth();
  const areaState = location.state?.area || {};
  const stateColegioId = location.state?.idColegio || null;

  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeColegioId = useMemo(() => stateColegioId || ColegioService.getActiveColegioId(), [stateColegioId]);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio';

  const [form, setForm] = useState({
    nombre: areaState.nombre || areaState.Nombre || '',
    descripcion: areaState.descripcion || areaState.Descripcion || '',
    idColegio: activeColegioId || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [adminsDisponibles, setAdminsDisponibles] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminsError, setAdminsError] = useState('');
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [adminsAsignados, setAdminsAsignados] = useState([]);
  const [isAssigning, setIsAssigning] = useState(false);

  useEffect(() => {
    if (!areaId) {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [areaId, navigate]);

  useEffect(() => {
    const fetchAdmins = async () => {
      setAdminsLoading(true);
      setAdminsError('');
      try {
        const list = await usuarioService.getAdminAreaUsersPorColegio(activeColegioId);
        setAdminsDisponibles(Array.isArray(list) ? list : []);
      } catch (error) {
        setAdminsError(error.message || 'No pudimos cargar los admins de área.');
      } finally {
        setAdminsLoading(false);
      }
    };

    if (activeColegioId) {
      fetchAdmins();
    }
  }, [activeColegioId]);

  useEffect(() => {
    const fetchArea = async () => {
      if (areaState && Object.keys(areaState).length > 0) return;
      if (!areaId) return;
      try {
        const a = await areaService.getAreaById(areaId);
        if (a) {
          setForm({
            nombre: a.nombre || a.Nombre || '',
            descripcion: a.descripcion || a.Descripcion || '',
            idColegio: a.idColegio || a.IdColegio || activeColegioId || ''
          });
          const adminsFromApi = a.adminsArea || a.AdminsArea || [];
          if (Array.isArray(adminsFromApi)) {
            setAdminsAsignados(adminsFromApi);
          }
        }
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar el área.');
        setMessageType('error');
      }
    };
    fetchArea();
  }, [areaId, areaState, activeColegioId]);

  useEffect(() => {
    const initialAdmins = areaState.adminsArea || areaState.AdminsArea || [];
    setAdminsAsignados(Array.isArray(initialAdmins) ? initialAdmins : []);
  }, [areaState]);

  useEffect(() => {
    if (activeColegioId && !form.idColegio) {
      setForm((prev) => ({ ...prev, idColegio: activeColegioId }));
    }
  }, [activeColegioId, form.idColegio]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddAdmin = () => {
    if (!selectedAdminId) return;
    const admin = adminsDisponibles.find((a) => (a.id || a.Id) === selectedAdminId);
    if (!admin) return;
    if (adminsAsignados.some((a) => (a.id || a.Id) === selectedAdminId)) return;
    setAdminsAsignados((prev) => [...prev, admin]);
    setSelectedAdminId('');
  };

  const handleRemoveAdmin = (id) => {
    setAdminsAsignados((prev) => prev.filter((a) => (a.id || a.Id) !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      setMessage('Completa el nombre del área.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Guardando cambios...');
    setMessageType('info');
    try {
      await areaService.updateArea(areaId, {
        nombre: form.nombre,
        descripcion: form.descripcion,
        idColegio: form.idColegio
      });
      setMessage('Área actualizada correctamente.');
      setMessageType('success');
      setTimeout(() => navigate('/admin-dashboard', { replace: true, state: { tab: 'areas' } }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos actualizar el área.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAssignAdmins = async () => {
    if (!areaId) return;
    const ids = adminsAsignados.map((a) => a.id || a.Id).filter(Boolean);
    if (ids.length === 0) {
      setMessage('Agrega al menos un admin de área para asociar.');
      setMessageType('error');
      return;
    }

    setIsAssigning(true);
    setMessage('Asociando admins al área...');
    setMessageType('info');
    try {
      const res = await areaService.assignAdmins(areaId, ids);
      const nuevos = res?.asociadosNuevos || res?.AsociadosNuevos || [];
      const actualizados = res?.actualizadosAdmin || res?.ActualizadosAdmin || [];
      const yaEran = res?.yaEranAdmin || res?.YaEranAdmin || [];
      const noEncontrados = res?.usuariosNoEncontrados || res?.UsuariosNoEncontrados || [];
      const noAdminArea = res?.usuariosNoAdminArea || res?.UsuariosNoAdminArea || [];

      const summaryParts = [];
      if (nuevos.length) summaryParts.push(`Asociados nuevos: ${nuevos.length}`);
      if (actualizados.length) summaryParts.push(`Actualizados a admin: ${actualizados.length}`);
      if (yaEran.length) summaryParts.push(`Ya eran admin: ${yaEran.length}`);
      if (noEncontrados.length) summaryParts.push(`No encontrados: ${noEncontrados.length}`);
      if (noAdminArea.length) summaryParts.push(`Sin rol Admin Área: ${noAdminArea.length}`);

      setMessage(`Asociación completada. ${summaryParts.join(' | ') || ''}`.trim());
      setMessageType('success');
    } catch (error) {
      setMessage(error.message || 'No pudimos asociar los admins.');
      setMessageType('error');
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div className="new-dashboard admin-mode">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        <AdminMenu
          activeId="areas"
          user={user}
          featureFlags={ColegioService.getFeatureFlags()}
          baseItems={ADMIN_FORM_MENU_ITEMS}
          onNavigate={(item) => navigateAdminMenuItem(navigate, item)}
        />
        <div className="sidebar-footer">
          <ActiveColegioBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Editar área</h1>
            <p className="page-subtitle">Ajusta los datos del área en el colegio {activeColegioName}.</p>
          </div>
          <div className="header-right">
            <ActiveColegioBadge />
            <UserSessionMenu user={user} onLogout={logout} showStandaloneLogout={false} userFallbackLabel="Administrador" />
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem' }}>
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-sitemap" />
                <span>Datos del área</span>
              </div>
            </div>
            <div className="card-content" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
              <div>
                <p style={{ marginTop: 0, color: '#475569' }}>
                  Completa o ajusta los datos del área.
                </p>
                <form onSubmit={handleSubmit} className="prof-create-form">
                  <div className="form-field">
                    <label>Nombre</label>
                    <input
                      type="text"
                      value={form.nombre}
                      onChange={(e) => handleChange('nombre', e.target.value)}
                      placeholder="Área X"
                    />
                  </div>
                  <div className="form-field">
                    <label>Descripción</label>
                    <textarea
                      value={form.descripcion}
                      onChange={(e) => handleChange('descripcion', e.target.value)}
                      placeholder="Descripción del área"
                      style={{ borderRadius: '10px', border: '1px solid var(--gray-200)', padding: '0.65rem 0.85rem', fontSize: '0.95rem', minHeight: '80px' }}
                    />
                  </div>

                  {message && (
                    <div
                      className="modal-message"
                      style={{
                        marginTop: '0.5rem',
                        borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                        background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                        color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                      }}
                    >
                      {isSubmitting && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                      {message}
                    </div>
                  )}

                  <div className="form-actions">
                    <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin-dashboard')}>
                      Cancelar
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                      {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                      <span>Guardar cambios</span>
                    </button>
                  </div>
                </form>
              </div>

              <div style={{ borderLeft: '1px solid var(--gray-200)', paddingLeft: '1.25rem' }} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminEditArea;
